import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/layout/Layout';
import Home from './components/pages/Home';
import About from './components/pages/About';
import Vision from './components/pages/Vision';
import Services from './components/pages/Services';
import ServiceDetail from './components/pages/ServiceDetail';
import Products from './components/pages/Products';
import ProductDetail from './components/pages/ProductDetail';
import Contact from './components/pages/Contact';
import CareerDetail from './components/pages/CareerDetail';
import Donation from './components/pages/Donation';
import AdminEditor from './components/pages/AdminEditor';
import ScrollToTop from './components/common/ScrollToTop';
import { BusinessProvider, useBusinessData } from './context/BusinessContext';
import './css/App.css';

// Check if admin route should be enabled (set at build time)
const ADMIN_ENABLED = process.env.REACT_APP_ADMIN_ENABLED === 'true';

// Create a component to handle title and favicon updates
const AppContent = () => {
  const { loading, getData, getImageUrl } = useBusinessData();

  useEffect(() => {
    if (!loading) {
      // Update the document title with the business name
      const businessName = getData('name', 'Yadunandan Gaulaxmi Aavas');
      document.title = businessName;
      
      // Update the meta description with the business description
      const businessDescription = getData('description', 'Business information and services');
      const metaDescription = document.getElementById('meta-description');
      if (metaDescription) {
        metaDescription.setAttribute('content', businessDescription);
      }
      
      // Update favicon if specified in the JSON
      // The favicon path in JSON is: business.images.favicon
      const customFavicon = getImageUrl('images.favicon', null);
      if (customFavicon) {
        const linkElement = document.querySelector("link[rel='icon']");
        if (linkElement) {
          // If it's a relative path, we need to keep the %PUBLIC_URL%
          // If it's an absolute URL (starts with http), use it directly
          if (customFavicon.startsWith('http')) {
            linkElement.href = customFavicon;
          } else if (customFavicon !== 'favicon.ico') {
            // Only update if it's not the default
            linkElement.href = `${process.env.PUBLIC_URL}/${customFavicon}`;
          }
        }
      }
      
      // Set dynamic primary and secondary colors from businessInfo
      const primaryColor = getData('ui.theme.primaryColor', '#4CAF50');
      const secondaryColor = getData('ui.theme.secondaryColor', '#333');
      const primaryColorDark = getData('ui.theme.primaryColorDark', '#388E3C');
      const logoColor = getData('ui.theme.logoColor', primaryColor); // Get logo color with fallback to primary color
      const navBackgroundColor = getData('ui.theme.navBackgroundColor', 'rgba(26, 26, 26, 0.9)');
      const navTextColor = getData('ui.theme.navTextColor', '#ffffff');
      
      // Apply to CSS custom properties (variables)
      document.documentElement.style.setProperty('--primary-color', primaryColor);
      document.documentElement.style.setProperty('--secondary-color', secondaryColor);
      document.documentElement.style.setProperty('--primary-color-dark', primaryColorDark);
      document.documentElement.style.setProperty('--logo-color', logoColor);
      document.documentElement.style.setProperty('--navbar-color', navBackgroundColor);
      document.documentElement.style.setProperty('--nav-text-color', navTextColor);
    }
  }, [loading, getData, getImageUrl]);

  return (
    <Router>
      <ScrollToTop />
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/vision" element={<Vision />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:productId" element={<ProductDetail />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:serviceId" element={<ServiceDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/careers/:jobId" element={<CareerDetail />} />
          <Route path="/donate" element={<Donation />} />
          {ADMIN_ENABLED && <Route path="/admin" element={<AdminEditor />} />}
        </Routes>
      </Layout>
    </Router>
  );
};

function App() {
  return (
    <BusinessProvider>
      <AppContent />
    </BusinessProvider>
  );
}

export default App;
